/**
 * Clamps long PDP “Why” tile copy and shows a toggle when content overflows.
 */
class PdpClampText extends HTMLElement {
  #copy;
  #button;
  #labelMore;
  #labelLess;
  #collapsed = true;
  #resizeObserver;
  #measuring = false;

  connectedCallback() {
    this.#copy = this.querySelector("[data-clamp-copy]");
    this.#button = this.querySelector("[data-clamp-toggle]");
    this.#labelMore = this.#button?.querySelector("[data-label-more]");
    this.#labelLess = this.#button?.querySelector("[data-label-less]");

    if (!this.#copy || !this.#button) {
      return;
    }

    this.#button.addEventListener("click", this.#onToggle);
    this.#resizeObserver = new ResizeObserver(() => {
      if (!this.#measuring && this.#collapsed) {
        this.#measure();
      }
    });
    this.#resizeObserver.observe(this);

    requestAnimationFrame(() => this.#measure());
  }

  disconnectedCallback() {
    this.#button?.removeEventListener("click", this.#onToggle);
    this.#resizeObserver?.disconnect();
  }

  #onToggle = () => {
    this.#collapsed = !this.#collapsed;
    this.#applyCollapsedState();
    this.#syncLabels();
  };

  #lineCount() {
    const raw = getComputedStyle(this).getPropertyValue("--pdp-clamp-lines").trim();
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 4;
  }

  #measure() {
    if (!this.#collapsed || this.#measuring) {
      return;
    }

    this.#measuring = true;

    // Measure natural height without clamp (line-clamp makes scrollHeight unreliable).
    this.removeAttribute("data-collapsed");
    const fullHeight = this.#copy.scrollHeight;

    const styles = getComputedStyle(this.#copy);
    let lineHeight = Number.parseFloat(styles.lineHeight);
    if (!Number.isFinite(lineHeight)) {
      lineHeight = Number.parseFloat(styles.fontSize) * 1.5;
    }

    const maxCollapsedHeight = lineHeight * this.#lineCount();
    const overflows = fullHeight > maxCollapsedHeight + 1;

    this.setAttribute("data-collapsed", "true");
    this.#button.hidden = !overflows;
    this.#button.setAttribute("aria-expanded", "false");
    this.#syncLabels();

    this.#measuring = false;
  }

  #applyCollapsedState() {
    if (this.#collapsed) {
      this.setAttribute("data-collapsed", "true");
      this.#button.setAttribute("aria-expanded", "false");
    } else {
      this.removeAttribute("data-collapsed");
      this.#button.setAttribute("aria-expanded", "true");
    }
  }

  #syncLabels() {
    if (!this.#labelMore || !this.#labelLess) {
      return;
    }

    this.#labelMore.hidden = !this.#collapsed;
    this.#labelLess.hidden = this.#collapsed;
  }
}

if (!window.customElements.get("pdp-clamp-text")) {
  window.customElements.define("pdp-clamp-text", PdpClampText);
}
